import { Injectable } from '@angular/core';
import { IDToken } from '@okta/okta-auth-js';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { JwtHelperService } from './jwthelper.service';
import { AppConstants } from '../constants';

export interface IdentityTokenResult {
	isValid: boolean;
	planNumbers?: string[];
}

@Injectable({
	providedIn: 'root',
})
export class PGHIdentityService {

	getIdToken(): IDToken {
		const tokendata = localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityTokenStorage);
		if (tokendata) {
			const parsedToken = JSON.parse(tokendata);
			if(parsedToken.hasOwnProperty("idToken"))
				return parsedToken['idToken'];
			return null;
		} else {
			return null;
		}
	}

	isTokenValid(): boolean {
		const jwtHelperService = new JwtHelperService();
		const token = this.getIdToken();
		if (token === null) {
			return false;
		}
		return !jwtHelperService.isTokenExpired(token);
	}

	getUser(): UserClaims | null {
		if (this.isTokenValid()) {
			const userData = localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityUser);
			if (userData) {
				return JSON.parse(userData);
			}
		}
		return null;
	}

	processIdentityToken(): IdentityTokenResult {
		const identityTokenRaw = localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityToken);
		if (!identityTokenRaw) {
			return { isValid: false };
		}

		try {
			const parts = identityTokenRaw.split('.');
			if (parts.length !== 3) {
				return { isValid: false };
			}

			const payload = JSON.parse(atob(parts[1]));
			const now = Math.floor(Date.now() / 1000);

			if (!payload.exp || payload.exp <= now || payload.sub !== AppConstants.IDENTITY_TOKEN.Subject || payload.iss !== AppConstants.IDENTITY_TOKEN.Issuer) {
				localStorage.removeItem(AppConstants.STORAGE_KEYS.IdentityToken);
				return { isValid: false };
			}

			// Build identity-token-storage compatible structure so downstream services work
			const tokenStorage = {
				idToken: {
					idToken: identityTokenRaw,
					claims: payload,
					expiresAt: payload.exp,
					authorizeUrl: '',
					issuer: payload.iss,
					clientId: ''
				}
			};
			localStorage.setItem(AppConstants.STORAGE_KEYS.IdentityTokenStorage, JSON.stringify(tokenStorage));

			// Build identity-user compatible structure. When the customer has no email on file, the
			// middleware puts a session-only pseudo email in LoggedInUserEmail (flagged by
			// IsSyntheticEmail) purely as its identifier — never surface it to the portal.
			const email = payload.IsSyntheticEmail === true ? '' : (payload.LoggedInUserEmail || '');
			const user = {
				sub: payload.sub || '',
				email,
				given_name: payload.FirstName || '',
				family_name: payload.LastName || '',
				preferred_username: email,
				name: `${payload.FirstName || ''} ${payload.LastName || ''}`.trim()
			};
			localStorage.setItem(AppConstants.STORAGE_KEYS.IdentityUser, JSON.stringify(user));

			if (Array.isArray(payload.PlanNumbers)) {
				return { isValid: true, planNumbers: payload.PlanNumbers };
			}

			localStorage.removeItem(AppConstants.STORAGE_KEYS.IdentityToken);
			return { isValid: false };
		} catch {
			localStorage.removeItem(AppConstants.STORAGE_KEYS.IdentityToken);
			return { isValid: false };
		}
	}
}
